import { random } from "lodash";
export function buildAnimatorFirework(
  canvas: HTMLCanvasElement
): null | number {
  console.log("buildAnimator", canvas.width, canvas.height);
  const ctx = canvas.getContext("2d", { alpha: true });
  if (ctx) {
    return window.requestAnimationFrame(buildStep(ctx));
  } else {
    return null;
  }
}

function hitTest(
  x1: number,
  y1: number,
  w1: number,
  h1: number,
  x2: number,
  y2: number,
  w2: number,
  h2: number
) {
  return !(x1 + w1 < x2 || x2 + w2 < x1 || y1 + h1 < y2 || y2 + h2 < y1);
}

function rand(min: number, max: number) {
  return random(min, max, false);
}

interface FireworkConfig {
  fworkSpeed: number;
  fworkAccel: number;
  partCount: number;
  partSpeed: number;
  partSpeedVariance: number;
  partWind: number;
  partFriction: number;
  partGravity: number;
  flickerDensity: number;
  hueMin: number;
  hueMax: number;
  hueVariance: number;
  lineWidth: number;
}

interface Particle {
  x: number;
  y: number;
  coordLast: {
    x: number;
    y: number;
  }[];
  angle: number;
  speed: number;
  friction: number;
  gravity: number;
  hue: number;
  brightness: number;
  alpha: number;
  decay: number;
  wind: number;
  lineWidth: number;
  radius: number;
  cfg: FireworkConfig;
}

interface Firework {
  x: number;
  y: number;
  startX: number;
  startY: number;
  hitX: boolean;
  hitY: boolean;
  coordLast: {
    x: number;
    y: number;
  }[];
  targetX: number;
  targetY: number;
  speed: number;
  angle: number;
  shockwaveAngle: number;
  acceleration: number;
  hue: number;
  brightness: number;
  alpha: number;
  lineWidth: number;
  cfg: FireworkConfig;
}

const Nimble: FireworkConfig = {
  fworkSpeed: 4,
  fworkAccel: 10,
  partCount: 120,
  partSpeed: 30,
  partSpeedVariance: 30,
  partWind: 100,
  partFriction: 50,
  partGravity: 0,
  flickerDensity: 50,
  hueMin: 0,
  hueMax: 360,
  hueVariance: 30,
  lineWidth: 1,
};

const Default: FireworkConfig = {
  fworkSpeed: 4,
  fworkAccel: 10,
  partCount: 150,
  partSpeed: 5,
  partSpeedVariance: 10,
  partWind: 50,
  partFriction: 5,
  partGravity: 8,
  flickerDensity: 25,
  hueMin: 0,
  hueMax: 360,
  hueVariance: 30,
  lineWidth: 1,
};

class Fireworks {
  particles: Particle[] = [];
  fireworks: Firework[] = [];
  fireworkConfig = [Nimble, Default];
  clearAlpha = 25;

  //   partSpeed = 5;
  //   partSpeedVariance = 10;
  //   partWind = 50;
  //   partFriction = 5;
  //   // partGravity = 1;
  //   partGravity = 5;
  //   hueMin = 0;
  //   hueMax = 360;
  //   fworkSpeed = 4;
  //   fworkAccel = 10;
  //   hueVariance = 30;
  //   flickerDensity = 25;
  //   clearAlpha = 25;
  //   lineWidth = 1;

  showShockwave = true;
  showTarget = false;

  constructor(private ctx: CanvasRenderingContext2D) {}

  get canvas(): HTMLCanvasElement {
    return this.ctx.canvas;
  }

  get mx() {
    return this.canvas.width / 2;
  }

  get my() {
    return this.canvas.height / 2;
  }

  get cw() {
    return this.canvas.width;
  }

  get ch() {
    return this.canvas.height;
  }

  init(time: number) {
    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
    this.bindEvents();
    this.canvasLoop(time);

    this.canvas.onselectstart = function () {
      return false;
    };
  }

  createParticles(x: number, y: number, hue: number, cfg: FireworkConfig) {
    let countdown = cfg.partCount;
    while (countdown--) {
      const newParticle: Particle = {
        cfg,
        x: x,
        y: y,
        coordLast: [
          { x: x, y: y },
          { x: x, y: y },
          { x: x, y: y },
        ],
        angle: rand(0, 360),
        speed: rand(
          cfg.partSpeed - cfg.partSpeedVariance <= 0
            ? 1
            : cfg.partSpeed - cfg.partSpeedVariance,
          cfg.partSpeed + cfg.partSpeedVariance
        ),
        friction: 1 - cfg.partFriction / 100,
        gravity: cfg.partGravity / 2,
        hue: rand(hue - cfg.hueVariance, hue + cfg.hueVariance),
        brightness: rand(50, 80),
        alpha: rand(40, 100) / 100,
        decay: rand(10, 50) / 1000,
        wind: (rand(0, cfg.partWind) - cfg.partWind / 2) / 25,
        lineWidth: cfg.lineWidth,
        radius: 1,
      };
      this.particles.push(newParticle);
    }
  }

  updateParticles(_time: number) {
    let i = this.particles.length;
    while (i--) {
      const p = this.particles[i];
      const radians = (p.angle * Math.PI) / 180;
      const vx = Math.cos(radians) * p.speed;
      const vy = Math.sin(radians) * p.speed;
      p.speed *= p.friction;

      p.coordLast[2].x = p.coordLast[1].x;
      p.coordLast[2].y = p.coordLast[1].y;
      p.coordLast[1].x = p.coordLast[0].x;
      p.coordLast[1].y = p.coordLast[0].y;
      p.coordLast[0].x = p.x;
      p.coordLast[0].y = p.y;

      p.x += vx;
      p.y += vy;
      p.y += p.gravity;

      p.angle += p.wind;
      p.alpha -= p.decay;

      if (
        !hitTest(
          0,
          0,
          this.cw,
          this.ch,
          p.x - p.radius,
          p.y - p.radius,
          p.radius * 2,
          p.radius * 2
        ) ||
        p.alpha < 0.05
      ) {
        this.particles.splice(i, 1);
      }
    }
  }

  /*=============================================================================*/
  /* Draw Particles
    /*=============================================================================*/
  drawParticles() {
    let i = this.particles.length;
    while (i--) {
      const p = this.particles[i];

      const coordRand = rand(1, 3) - 1;
      this.ctx.beginPath();
      this.ctx.moveTo(
        Math.round(p.coordLast[coordRand].x),
        Math.round(p.coordLast[coordRand].y)
      );
      this.ctx.lineTo(Math.round(p.x), Math.round(p.y));
      this.ctx.closePath();
      this.ctx.strokeStyle = `hsla(${p.hue}, 100%, ${p.brightness}%, ${p.alpha})`;
      this.ctx.stroke();

      if (p.cfg.flickerDensity > 0) {
        const inverseDensity = 50 - p.cfg.flickerDensity;
        if (rand(0, inverseDensity) === inverseDensity) {
          this.ctx.beginPath();
          this.ctx.arc(
            Math.round(p.x),
            Math.round(p.y),
            rand(p.lineWidth, p.lineWidth + 3) / 2,
            0,
            Math.PI * 2,
            false
          );
          this.ctx.closePath();
          const randAlpha = rand(50, 100) / 100;
          this.ctx.fillStyle = `hsla(${p.hue}, 100%, ${p.brightness}%, ${randAlpha})`;
          this.ctx.fill();
        }
      }
    }
  }

  /*=============================================================================*/
  /* Create Fireworks
    /*=============================================================================*/
  createFireworks(
    startX: number,
    startY: number,
    targetX: number,
    targetY: number,
    hue: number,
    cfg: FireworkConfig
  ) {
    const newFirework: Firework = {
      x: startX,
      y: startY,
      startX: startX,
      startY: startY,
      hitX: false,
      hitY: false,
      coordLast: [
        { x: startX, y: startY },
        { x: startX, y: startY },
        { x: startX, y: startY },
      ],
      targetX: targetX,
      targetY: targetY,
      speed: cfg.fworkSpeed,
      angle: Math.atan2(targetY - startY, targetX - startX),
      shockwaveAngle:
        Math.atan2(targetY - startY, targetX - startX) + 90 * (Math.PI / 180),
      acceleration: cfg.fworkAccel / 100,
      hue,
      brightness: rand(50, 80),
      alpha: rand(50, 100) / 100,
      lineWidth: cfg.lineWidth,
      cfg,
    };
    this.fireworks.push(newFirework);
  }

  /*=============================================================================*/
  /* Update Fireworks
    /*=============================================================================*/
  updateFireworks(_time: number) {
    let i = this.fireworks.length;
    while (i--) {
      const f = this.fireworks[i];
      this.ctx.lineWidth = f.lineWidth;

      const vx = Math.cos(f.angle) * f.speed;
      const vy = Math.sin(f.angle) * f.speed;
      f.speed *= 1 + f.acceleration;
      f.coordLast[2].x = f.coordLast[1].x;
      f.coordLast[2].y = f.coordLast[1].y;
      f.coordLast[1].x = f.coordLast[0].x;
      f.coordLast[1].y = f.coordLast[0].y;
      f.coordLast[0].x = f.x;
      f.coordLast[0].y = f.y;

      if (f.startX >= f.targetX) {
        if (f.x + vx <= f.targetX) {
          f.x = f.targetX;
          f.hitX = true;
        } else {
          f.x += vx;
        }
      } else {
        if (f.x + vx >= f.targetX) {
          f.x = f.targetX;
          f.hitX = true;
        } else {
          f.x += vx;
        }
      }

      if (f.startY >= f.targetY) {
        if (f.y + vy <= f.targetY) {
          f.y = f.targetY;
          f.hitY = true;
        } else {
          f.y += vy;
        }
      } else {
        if (f.y + vy >= f.targetY) {
          f.y = f.targetY;
          f.hitY = true;
        } else {
          f.y += vy;
        }
      }

      if (f.hitX && f.hitY) {
        this.createParticles(f.targetX, f.targetY, f.hue, f.cfg);
        this.fireworks.splice(i, 1);
      }
    }
  }

  /*=============================================================================*/
  /* Draw Fireworks
    /*=============================================================================*/
  drawFireworks() {
    let i = this.fireworks.length;
    this.ctx.globalCompositeOperation = "lighter";
    while (i--) {
      const f = this.fireworks[i];
      this.ctx.lineWidth = f.lineWidth;

      const coordRand = rand(1, 3) - 1;
      this.ctx.beginPath();
      this.ctx.moveTo(
        Math.round(f.coordLast[coordRand].x),
        Math.round(f.coordLast[coordRand].y)
      );
      this.ctx.lineTo(Math.round(f.x), Math.round(f.y));
      this.ctx.closePath();
      this.ctx.strokeStyle = `hsla(${f.hue}, 100%, ${f.brightness}%, ${f.alpha})`;
      this.ctx.stroke();

      if (this.showTarget) {
        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.arc(
          Math.round(f.targetX),
          Math.round(f.targetY),
          rand(1, 8),
          0,
          Math.PI * 2,
          false
        );
        this.ctx.closePath();
        this.ctx.lineWidth = 1;
        this.ctx.stroke();
        this.ctx.restore();
      }

      if (this.showShockwave) {
        this.ctx.save();
        this.ctx.translate(Math.round(f.x), Math.round(f.y));
        this.ctx.rotate(f.shockwaveAngle);
        this.ctx.beginPath();
        this.ctx.arc(0, 0, 1 * (f.speed / 5), 0, Math.PI, true);
        this.ctx.strokeStyle = `hsla(${f.hue}, 100%, ${f.brightness}%, ${
          rand(25, 60) / 100
        })`;
        this.ctx.lineWidth = f.lineWidth;
        this.ctx.stroke();
        this.ctx.restore();
      }
    }
  }

  /*=============================================================================*/
  /* Events
    /*=============================================================================*/
  bindEvents() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    this.canvas.addEventListener(
      "mousedown",
      function (ev) {
        const mx = ev.pageX - self.canvas.offsetLeft;
        const my = ev.pageY - self.canvas.offsetTop;

        self.createFireworks(
          self.cw / 2,
          self.ch,
          mx,
          my,
          rand(Default.hueMin, Default.hueMax),
          Default
        );
      },
      { passive: true }
    );

    /*
        $(window).on('resize', function () {
            clearTimeout(self.timeout);
            self.timeout = setTimeout(function () {
                self.canvas.width = self.cw = $(window).innerWidth();
                self.canvas.height = self.ch = $(window).innerHeight();
                self.ctx.lineCap = 'round';
                self.ctx.lineJoin = 'round';
            }, 100);
        });

        $(self.canvas).on('mousedown', function (e) {
            self.mx = e.pageX - self.canvas.offsetLeft;
            self.my = e.pageY - self.canvas.offsetTop;
            self.currentHue = rand(self.hueMin, self.hueMax);
            self.createFireworks(self.cw / 2, self.ch, self.mx, self.my);

            $(self.canvas).on('mousemove.fireworks', function (e) {
                self.mx = e.pageX - self.canvas.offsetLeft;
                self.my = e.pageY - self.canvas.offsetTop;
                self.currentHue = rand(self.hueMin, self.hueMax);
                self.createFireworks(self.cw / 2, self.ch, self.mx, self.my);
            });
        });

        $(self.canvas).on('mouseup', function (e) {
            $(self.canvas).off('mousemove.fireworks');
        });
        */
  }

  /*=============================================================================*/
  /* Clear Canvas
    /*=============================================================================*/
  clear() {
    this.particles = [];
    this.fireworks = [];
    this.ctx.clearRect(0, 0, this.cw, this.ch);
  }

  /*=============================================================================*/
  /* Main Loop
    /*=============================================================================*/
  canvasLoop(time: number) {
    // requestAnimFrame(this.canvasLoop, this.canvas);
    this.ctx.globalCompositeOperation = "destination-out";
    this.ctx.fillStyle = `rgba(0,0,0,${this.clearAlpha / 100})`;
    this.ctx.fillRect(0, 0, this.cw, this.ch);
    this.updateFireworks(time);
    this.updateParticles(time);
    this.drawFireworks();
    this.drawParticles();
  }
}

function fireFirework(fireworks: Fireworks) {
  function fire() {
    const cfg =
      fireworks.fireworkConfig[rand(0, fireworks.fireworkConfig.length - 1)];
    fireworks.createFireworks(
      fireworks.cw / 2 -
        rand(-Math.trunc(fireworks.cw * 0.3), Math.trunc(fireworks.cw * 0.3)),
      fireworks.ch,
      rand(Math.trunc(fireworks.cw * 0.1), Math.trunc(fireworks.cw * 0.9)),
      rand(Math.trunc(fireworks.ch * 0.15), Math.trunc(fireworks.ch * 0.5)),
      rand(cfg.hueMin, cfg.hueMax),
      cfg
    );
    window.setTimeout(fire, rand(200, 500));
  }
  window.setTimeout(fire, rand(500, 1000));
}

function buildStep(ctx: CanvasRenderingContext2D): (time: number) => void {
  //   const w = ctx.canvas.width;
  //   const h = ctx.canvas.height;
  const fireworks = new Fireworks(ctx);
  fireworks.init(performance.now());
  const fireworks1 = new Fireworks(ctx);

  fireworks1.init(performance.now());

  fireFirework(fireworks);

  function step(time: number) {
    fireworks.canvasLoop(time);
    fireworks1.canvasLoop(time);
    // ctx.clearRect(0, 0, w, h);
    // ctx.save()
    // ctx.strokeStyle = "red";
    // ctx.lineWidth = 23;
    // ctx.moveTo(0, 0)
    // ctx.lineTo(50, 50);
    // ctx.stroke();

    // ctx.restore()

    window.requestAnimationFrame(step);
  }
  return step;
}
